import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authService } from "../api/services/authService";
import { identityService } from "../api/services/identityService";
import { registerUnauthorizedHandler, tokenStorage, userStorage } from "../api/apiClient";

const AuthContext = createContext(null);

// hrms_enabled/ats_enabled/plan/company_name live on the /auth/login response's
// `user` object (persisted to userStorage), not on the ATS /me endpoint --
// merge them in wherever `me` gets set.
const withCompanyInfo = (profile) => {
  const loginUser = userStorage.get();
  return {
    ...profile,
    hrms_enabled: Boolean(loginUser?.hrms_enabled),
    ats_enabled: loginUser?.ats_enabled,
    plan: loginUser?.plan,
    company_name: loginUser?.company_name,
  };
};

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
  const [me, setMe] = useState(null); // { user_type, employee_id, role_code, permissions, name, email, hrms_enabled, ... }
  const [meLoading, setMeLoading] = useState(false);

  const loadMe = useCallback(async () => {
    setMeLoading(true);
    try {
      const data = await identityService.getMe();
      setMe(withCompanyInfo(data));
    } catch {
      setMe(null);
    } finally {
      setMeLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setIsAuthenticated(false);
    setMe(null);
  }, []);

  useEffect(() => {
    // any 401 from the API forces the app back to a logged-out state,
    // instead of every page having to catch it individually
    registerUnauthorizedHandler(() => {
      setIsAuthenticated(false);
      setMe(null);
    });
  }, []);

  useEffect(() => {
    if (isAuthenticated) loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  /**
   * Credentials are validated by HRMS's own /auth/login, which knows
   * nothing about ATS -- it happily issues a token to anyone with a valid
   * HRMS account. So a successful login is NOT the same as having ATS
   * access.
   *
   * GET /api/v1/ats/me is the real gate: it 403s with a specific reason if
   * the company doesn't have ATS enabled, or if this person has no ATS
   * role. Checking it here (before the caller navigates) means the user
   * sees a clear message on the login screen rather than landing on a
   * dashboard that then fails every request with raw 403s.
   *
   * On failure the token is discarded, so there's no half-logged-in state.
   */
  const login = async (email, password) => {
    await authService.login(email, password);

    let profile;
    try {
      profile = await identityService.getMe();
    } catch (err) {
      authService.logout(); // don't leave a token behind for a session that can't be used
      const detail = err.response?.data?.detail || "";
      const status = err.response?.status;

      if (status === 403 && detail.includes("ATS is not enabled")) {
        throw new Error(
          "This company doesn't have ATS enabled. Please contact your organization's administrator."
        );
      }
      if (status === 403 && detail.includes("No ATS role assigned")) {
        throw new Error(
          "Your account doesn't have access to ATS yet. Please ask your administrator to assign you a role."
        );
      }
      if (status === 403 && detail.includes("No employee profile found")) {
        throw new Error(
          "No employee profile is linked to your account. Please contact your HR administrator."
        );
      }
      if (status === 403) {
        throw new Error(detail || "You don't have access to ATS.");
      }
      throw new Error("Signed in, but couldn't load your ATS profile. Please try again.");
    }

    setMe(withCompanyInfo(profile));
    setIsAuthenticated(true);
  };

  const hasPermission = (code) => Boolean(me?.permissions?.includes(code));

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, login, logout, token: tokenStorage.get(), me, meLoading, hasPermission }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}